import type {ComponentSingleStyleConfig} from '@chakra-ui/react';
import {getToken} from '@chakra-ui/react';
import {defineStyle} from '@chakra-ui/styled-system';
import type {ThemingProps} from '@chakra-ui/system';
import {useStyleConfig} from '@chakra-ui/system';
import {mode} from '@chakra-ui/theme-tools';

import {components as darkComponents} from '../../../design-tokens/dark';
import {components} from '../../../design-tokens/light';

const buttonPrimary = components.colors.components.buttons.primary;
const buttonPrimaryDark = darkComponents.colors.components.buttons.primary;
const buttonSecondary = components.colors.components.buttons.secondary;
const buttonSecondaryDark = darkComponents.colors.components.buttons.secondary;
const buttonSecondaryDanger = components.colors.components.buttons[`danger-secondary`];
const buttonSecondaryDangerDark = darkComponents.colors.components.buttons[`danger-secondary`];
const buttonOutline = components.colors.components.buttons.outline;
const buttonOutlineDark = darkComponents.colors.components.buttons.outline;
const buttonSubtle = components.colors.components.buttons.subtle;
const buttonSubtleDark = darkComponents.colors.components.buttons.subtle;

const variantPrimary = defineStyle(props => {
  const disabled = {
    bg: mode(buttonPrimary[`background-disabled`], buttonPrimaryDark[`background-disabled`])(props),
    color: mode(buttonPrimary[`text-disabled`], buttonPrimaryDark[`text-disabled`])(props),
  };

  return {
    bg: mode(buttonPrimary[`background-default`], buttonPrimaryDark[`background-default`])(props),
    color: buttonPrimary[`text-default`],
    // TODO: consume as token
    boxShadow: `0px 2px 6px -2px #0000000F, 0px 8px 12px -2px #00000014`,

    _hover: {
      bg: mode(buttonPrimary[`background-hover`], buttonPrimaryDark[`background-hover`])(props),
      _disabled: disabled,
    },
    _active: {
      bg: mode(buttonPrimary[`background-pressed`], buttonPrimaryDark[`background-pressed`])(props),
    },
    _focus: {
      bg: mode(buttonPrimary[`background-focus`], buttonPrimaryDark[`background-focus`])(props),
    },
    _disabled: disabled,
  };
});

const variantSecondary = defineStyle(props => {
  const {colorScheme} = props;
  const lightToken = colorScheme === `danger` ? buttonSecondaryDanger : buttonSecondary;
  const darkToken = colorScheme === `danger` ? buttonSecondaryDangerDark : buttonSecondaryDark;

  const disabled = {
    bg: mode(lightToken[`background-disabled`], darkToken[`background-disabled`])(props),
    color: mode(lightToken[`text-disabled`], darkToken[`text-disabled`])(props),
  };

  return {
    bg: mode(lightToken[`background-default`], darkToken[`background-default`])(props),
    color: mode(lightToken[`text-default`], darkToken[`text-default`])(props),
    _hover: {
      bg: mode(lightToken[`background-hover`], darkToken[`background-hover`])(props),
      _disabled: disabled,
    },
    _active: {
      bg: mode(lightToken[`background-pressed`], darkToken[`background-pressed`])(props),
    },
    _focus: {
      bg: mode(lightToken[`background-focus`], darkToken[`background-focus`])(props),
    },
    _disabled: disabled,
  };
});

const variantLink = defineStyle(props => {
  return {
    padding: 0,
    height: `auto`,
    lineHeight: `normal`,
    verticalAlign: `baseline`,
    color: mode(buttonPrimary[`background-hover`], buttonPrimaryDark[`background-hover`])(props),
    _hover: {
      textDecoration: `underline`,
      _disabled: {
        textDecoration: `none`,
      },
    },
    _active: {
      color: mode(buttonPrimary[`background-pressed`], buttonPrimaryDark[`background-pressed`])(props),
    },
  };
});

const variantOutlined = defineStyle(props => {
  const getBoxShadow = (extra?: string) =>
    [extra, `0 1px 2px 0 rgba(0, 0, 0, 0.04)`, `0 1px 3px 0 rgba(0, 0, 0, 0.08)`].filter(Boolean).join(`, `);
  const disabled = {
    color: mode(buttonOutline[`text-disabled`], buttonOutlineDark[`text-disabled`])(props),
    bg: mode(buttonOutline[`background-disabled`], buttonOutlineDark[`background-disabled`])(props),
    borderColor: `transparent`,
    boxShadow: getBoxShadow(),
  };

  return {
    border: `1px solid`,
    borderColor: `transparent`,
    color: mode(buttonOutline[`text-default`], buttonOutlineDark[`text-default`])(props),
    bg: mode(buttonOutline[`background-default`], buttonOutlineDark[`background-default`])(props),
    boxShadow: getBoxShadow(),

    _hover: {
      color: mode(buttonOutline[`text-hover`], buttonOutlineDark[`text-hover`])(props),
      bg: mode(buttonOutline[`background-hover`], buttonOutlineDark[`background-hover`])(props),
      borderColor: `transparent`,
      _disabled: disabled,
    },
    _focusVisible: {
      color: mode(buttonOutline[`text-default`], buttonOutlineDark[`text-default`])(props),
      bg: mode(buttonOutline[`background-focus`], buttonOutlineDark[`background-focus`])(props),
      borderColor: `transparent`,
      boxShadow: getBoxShadow(
        `0 0 0 2px ${getToken(
          `colors`,
          mode(buttonOutline[`stroke-focus`], buttonOutlineDark[`stroke-focus`])(props),
        )(props.theme)}`,
      ),
      _disabled: disabled,
    },
    _active: {
      color: mode(buttonOutline[`text-pressed`], buttonOutlineDark[`text-pressed`])(props),
      bg: mode(buttonOutline[`background-pressed`], buttonOutlineDark[`background-pressed`])(props),
      borderColor: mode(buttonOutline[`stroke-hover`], buttonOutlineDark[`stroke-hover`])(props),
      _disabled: disabled,
    },
    _disabled: disabled,
  };
});

const variantSubtle = defineStyle(props => {
  const disabled = {
    bg: mode(buttonSubtle[`background-disabled`], buttonSubtleDark[`background-disabled`])(props),
    color: mode(buttonSubtle[`text-disabled`], buttonSubtleDark[`text-disabled`])(props),
  };
  const colorDefault = mode(buttonSubtle[`text-default`], buttonSubtleDark[`text-default`])(props);

  return {
    color: colorDefault,
    bg: mode(buttonSubtle[`background-default`], buttonSubtleDark[`background-default`])(props),
    _hover: {
      color: mode(buttonSubtle[`text-hover`], buttonSubtleDark[`text-hover`])(props),
      bg: mode(buttonSubtle[`background-hover`], buttonSubtleDark[`background-hover`])(props),
      _disabled: disabled,
    },
    _focusVisible: {
      borderColor: mode(buttonSubtle[`stroke-focus`], buttonSubtleDark[`stroke-focus`])(props),
    },
    _focus: {
      color: colorDefault,
      borderColor: mode(buttonSubtle[`stroke-focus`], buttonSubtleDark[`stroke-focus`])(props),
      bg: mode(buttonSubtle[`background-focus`], buttonSubtleDark[`background-focus`])(props),
    },
    _active: {
      borderColor: `transparent`,
      color: mode(buttonSubtle[`text-pressed`], buttonSubtleDark[`text-pressed`])(props),
      bg: mode(buttonSubtle[`background-pressed`], buttonSubtleDark[`background-pressed`])(props),
    },
    _disabled: disabled,
  };
});

const ButtonTheme = {
  baseStyle: defineStyle(props => ({
    py: `space-050`,
    px: `space-050`,
    lineHeight: `1.2`,
    borderRadius: `radius-rounded`,
    fontWeight: `normal`,
    height: `40`,
    _focusVisible: {
      borderColor: mode(buttonOutline[`stroke-focus`], buttonOutlineDark[`stroke-focus`])(props),
      boxShadow: `none !important`,
    },
    _focus: {
      borderColor: mode(buttonOutline[`stroke-focus`], buttonOutlineDark[`stroke-focus`])(props),
    },
    _disabled: {
      opacity: 1,
      cursor: `not-allowed`,
      boxShadow: `none`,
    },
    _hover: {
      textDecoration: `none`,
      _disabled: {
        bg: `initial`,
      },
    },
  })),
  variants: {
    primary: variantPrimary,
    outline: variantOutlined,
    secondary: variantSecondary,
    link: variantLink,
    subtle: variantSubtle,
  },

  sizes: {
    xs: {
      height: `24`,
      px: `space-100`,
    },
    sm: {
      height: `32`,
      px: `space-150`,
    },
    md: {
      height: `40`,
      px: `space-200`,
    },
    lg: {
      height: `48`,
      px: `space-250`,
    },
  },
  defaultProps: {
    variant: `primary`,
  },
} satisfies ComponentSingleStyleConfig;

export const useButtonStyleConfig = (props: ThemingProps) => {
  return useStyleConfig(`Button`, props ?? {});
};

export default ButtonTheme;
